import { useState } from 'react';
import {
  /*TextField,*/ Checkbox,
  InputToggle,
} from '@calefy-inc/informedMaterial';
import { InformedTextField as TextField } from '../../InformedMaterialFills/InformedMaterialTextField';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import { withStyles } from '@mui/styles';
import { ArrayField } from 'informed';
import RenderedQuestionInstance from '../RenderedQuestionInstance';
import { SameAsMailingAddressButton } from '../SameAsMailingAddressButton';
import { SameAsPrimaryAddressButton } from '../SameAsPrimaryAddressButton';
import CloseIcon from '@mui/icons-material/Close';
import { formatAsReadableList } from '../utility';
import { AddItemButton } from '../common';
import { LocationWrapper } from './LocationWrapper';
import { AddressComponent } from './AddressComponent';

// types and classes
import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import { ProgramBuilderQuestionInstance } from '../../../FormManager/classes';
import {
  QuoteComponentProps,
  ProgramBuilderComponentProps,
  QuoteComponentValidationFunctionReturn,
} from '../types';
import { Component } from '../classes';
import { useUsingAxisSettings } from '../../../../hooks';
import { provinceOptions } from '../../../util/Constants/Province';
import { requiredComponents, findRequiredSubQuestion } from './utility';

const ProgramBuilderComponent = ({
  classes = {},
  questionInstance,
}: ProgramBuilderComponentProps) => {
  const propsBlob =
    questionInstance && questionInstance.propsBlob
      ? questionInstance.propsBlob
      : {};
  const repeatable = propsBlob.repeatable ? true : false;

  return (
    <>
      <Checkbox
        classes={classes}
        className={classes.checkbox}
        name='props_blob_repeatable'
        label='Repeatable set'
        value={repeatable}
      />
    </>
  );
};

const QuoteComponentWithoutStyles = function ({
  questionInstance,
  blockAddSubquestions,
  classes = {},
  keepStateIfRelevant = false,
  ...props
}: QuoteComponentProps) {
  const { repeatable } = questionInstance.propsBlob;

  let otherSubQuestions = questionInstance.subQuestions.filter(
    (ele) => !requiredComponents.map((q) => q.apiName).includes(ele.apiName),
  );

  const [justAddedIndex, setJustAddedIndex] = useState<number | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const onAxis = useUsingAxisSettings();

  return (
    <div className={classes.addressContainer}>
      <InputLabel className={classes.inputlabel}>
        {questionInstance.getQuoteQuestionLabel()}
      </InputLabel>
      <ArrayField name={questionInstance.apiName}>
        {({ fields, add }) => {
          if (fields.length === 0 && openIndex === null) {
            setJustAddedIndex(0);
            setOpenIndex(0);
            // this is to ensure that the state change (openIndex, &c.) happens before the new location is rendered
            add();
          }

          return (
            <>
              <ArrayField.Items>
                {({ name, index, remove }) => {
                  const address = findRequiredSubQuestion(
                    questionInstance,
                    'address',
                  );
                  const city = findRequiredSubQuestion(
                    questionInstance,
                    'city',
                  );
                  const province = findRequiredSubQuestion(
                    questionInstance,
                    'province',
                  );
                  const postal = findRequiredSubQuestion(
                    questionInstance,
                    'postal',
                  );

                  [
                    // now set the field prefix on all of these
                    address,
                    city,
                    province,
                    postal,
                  ].forEach((ele) => {
                    ele.fieldPrefix = name;
                  });

                  return (
                    <>
                      <LocationWrapper
                        questionInstance={questionInstance}
                        justAdded={index === justAddedIndex}
                        setJustAdded={(b: boolean) => {
                          if (!b) {
                            setJustAddedIndex(null);
                          }
                        }}
                        openIndex={openIndex}
                        setOpenIndex={(i: number | null) => setOpenIndex(i)}
                        fields={fields}
                      >
                        <Grid>
                          {fields.length > 1 ? (
                            <Typography
                              variant='subtitle1'
                              color='primary'
                              className={classes.center}
                            >
                              Address {index + 1}
                            </Typography>
                          ) : null}
                          <div className={classes.addressHeader}>
                            <div className={classes.sameAsMailingAddressButton}>
                              <SameAsMailingAddressButton
                                fillFields={{
                                  address: address.generateFieldName(),
                                  city: city.generateFieldName(),
                                  province: province.generateFieldName(),
                                  postalCode: postal.generateFieldName(),
                                }}
                                questionInstanceApiName={
                                  questionInstance.apiName
                                }
                              />
                              <SameAsPrimaryAddressButton
                                fillFields={{
                                  address: address.generateFieldName(),
                                  city: city.generateFieldName(),
                                  province: province.generateFieldName(),
                                  postalCode: postal.generateFieldName(),
                                }}
                                questionInstanceApiName={
                                  questionInstance.apiName
                                }
                              />
                            </div>
                            <div className={classes.removeAddress}>
                              {fields.length > 1 ? (
                                <Tooltip title='Remove Address'>
                                  <IconButton
                                    color='default'
                                    size='small'
                                    onClick={() => {
                                      remove();
                                    }}
                                    type='button'
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </div>
                          </div>
                          <>
                            <AddressComponent
                              label={address.getQuoteQuestionLabel(
                                address.required || questionInstance.required,
                              )}
                              name={address.apiName}
                              id={address.apiName}
                              helperText={address.helpText}
                              required={
                                address.required || questionInstance.required
                              }
                              className={classes.textfield}
                              classes={classes}
                              keepStateIfRelevant={true}
                              {...props}
                            />
                            <TextField
                              label={city.getQuoteQuestionLabel(
                                city.required || questionInstance.required,
                              )}
                              name={city.apiName}
                              id={city.apiName}
                              helperText={city.helpText}
                              required={
                                city.required || questionInstance.required
                              }
                              className={classes.textfield}
                              classes={classes}
                              keepStateIfRelevant={true}
                              {...props}
                              variant='standard'
                            />
                          </>
                          <Grid
                            container
                            direction='row'
                            justifyContent='space-between'
                            spacing={0}
                          >
                            {onAxis ? (
                              <>
                                <Grid item xs={12}>
                                  <InputToggle
                                    label={province.getQuoteQuestionLabel(
                                      province.required ||
                                        questionInstance.required,
                                    )}
                                    name={province.apiName}
                                    id={province.apiName}
                                    helperText={province.helpText}
                                    required={
                                      province.required ||
                                      questionInstance.required
                                    }
                                    className={classes.textfield}
                                    classes={classes}
                                    keepStateIfRelevant={true}
                                    options={provinceOptions}
                                    {...props}
                                    variant='standard'
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    label={postal.getQuoteQuestionLabel(
                                      postal.required ||
                                        questionInstance.required,
                                    )}
                                    name={postal.apiName}
                                    id={postal.id}
                                    helperText={postal.helpText}
                                    required={
                                      postal.required ||
                                      questionInstance.required
                                    }
                                    className={classes.textfield}
                                    classes={classes}
                                    keepStateIfRelevant={true}
                                    {...props}
                                    variant='standard'
                                  />
                                </Grid>
                              </>
                            ) : (
                              <>
                                <Grid item xs={12} md={5}>
                                  <TextField
                                    label={province.getQuoteQuestionLabel(
                                      province.required ||
                                        questionInstance.required,
                                    )}
                                    name={province.apiName}
                                    id={province.apiName}
                                    helperText={province.helpText}
                                    required={
                                      province.required ||
                                      questionInstance.required
                                    }
                                    className={classes.textfield}
                                    classes={classes}
                                    keepStateIfRelevant={true}
                                    {...props}
                                    variant='standard'
                                  />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <TextField
                                    label={postal.getQuoteQuestionLabel(
                                      postal.required ||
                                        questionInstance.required,
                                    )}
                                    name={postal.apiName}
                                    id={postal.id}
                                    helperText={postal.helpText}
                                    required={
                                      postal.required ||
                                      questionInstance.required
                                    }
                                    className={classes.textfield}
                                    classes={classes}
                                    keepStateIfRelevant={true}
                                    {...props}
                                    variant='standard'
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>

                          {otherSubQuestions
                            ? otherSubQuestions.map((subq) => {
                                subq.fieldPrefix = name;
                                return (
                                  <RenderedQuestionInstance
                                    questionInstance={subq}
                                    key={subq.id}
                                    classes={classes}
                                    keepStateIfRelevant={true}
                                  />
                                );
                              })
                            : null}
                        </Grid>
                      </LocationWrapper>
                    </>
                  );
                }}
              </ArrayField.Items>
              {repeatable && !blockAddSubquestions ? (
                <AddItemButton
                  text='Add Address'
                  aria-label='Add Address'
                  className={classes.button}
                  type='button'
                  color='primary'
                  onClick={() => {
                    const newIndex = fields.length;
                    add();
                    setJustAddedIndex(newIndex);
                    setOpenIndex(fields.length);
                  }}
                  size='large'
                />
              ) : null}
            </>
          );
        }}
      </ArrayField>
    </div>
  );
};

const QuoteComponent = withStyles((_theme: $TSFixMe) => ({}))(
  (props: QuoteComponentProps) => <QuoteComponentWithoutStyles {...props} />,
);

const gatherSubQuestions = (
  _formState: $TSFixMe,
  questionInstance: $TSFixMe,
) => {
  questionInstance = initialSubQuestions(questionInstance);
  return questionInstance;
};

const initialSubQuestions = (
  questionInstance: ProgramBuilderQuestionInstance,
) => {
  questionInstance.subQuestions = [
    ...requiredComponents,
    ...questionInstance.subQuestions.filter(
      (subq) =>
        !requiredComponents.map((q) => q.apiName).includes(subq.apiName),
    ),
  ];
  questionInstance.children = questionInstance.subQuestions.map((subq) =>
    subq.copy(),
  );
  return questionInstance;
};

export function validateLocation(
  questionInstance: ProgramBuilderQuestionInstance,
): QuoteComponentValidationFunctionReturn {
  const requiredSubquestionNames = requiredComponents.map((q) => q.apiName);
  const errorMessages = [];
  const missingComponentApiNames = requiredSubquestionNames.filter(
    (apiName) =>
      !questionInstance.subQuestions.find((subq) => subq.apiName === apiName),
  );
  if (missingComponentApiNames.length > 0) {
    const plural = missingComponentApiNames.length > 1;
    errorMessages.push(
      `Location component with name "${questionInstance.generateDisplayName()}" is missing required subquestion${
        plural ? 's' : ''
      } with name${plural ? 's' : ''} ${formatAsReadableList(
        missingComponentApiNames.map((s) => `"${s}"`),
      )}. The safest thing to do is to delete the question and re-create it to avoid problems later.`,
    );
  }
  return errorMessages;
}

const IconComponent = () => {
  return <LocationOnRoundedIcon fontSize='small' color='primary' />;
};

export default function createComponentRegistry() {
  return new Component({
    type: 'location',
    typeLabel: 'Location',
    gatherSubQuestions: gatherSubQuestions,
    initialSubQuestions: initialSubQuestions,
    // @ts-expect-error
    quoteComponent: QuoteComponent,
    dataType: 'nested',
    programBuilderComponent: ProgramBuilderComponent,
    programBuilderValidation: validateLocation,
    icon: IconComponent,
  });
}

export { QuoteComponent };
